/**
 * TermsOverlay - Legal Terms & Conditions Acceptance
 * Must be accepted before using the app
 */

import React, { useState, useEffect } from 'react';
import { Modal, ModalDialog, Box, Typography, Button, Checkbox } from '@mui/joy';
import { Gavel } from '@mui/icons-material';
import { supabase } from '../utils/supabaseClient';
import TermsContent from './TermsContent';

interface TermsOverlayProps {
  userId: string;
  userEmail?: string;
  currentVersion: string;
  onAccept: () => void;
  onDecline: () => void;
}

const CURRENT_TERMS_VERSION = '1.0'; // Kept as fallback only — currentVersion prop takes precedence

export default function TermsOverlay({ userId, userEmail, currentVersion = CURRENT_TERMS_VERSION, onAccept, onDecline }: TermsOverlayProps) {
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80 && !hasScrolled) {
      setHasScrolled(true);
    }
  };

  const handleAccept = async () => {
    if (!acceptTerms) return;

    setIsSubmitting(true);

    // Mark acceptance as pending BEFORE granting access — ensures retry on next login
    // if the background write fails (cleared in App.tsx handleTermsAccept on success)
    const pendingKey = `terms_pending_${userId}`;
    localStorage.setItem(pendingKey, '1');

    // Immediately grant access - don't wait for database
    onAccept();

    // Record acceptance in background (fire and forget)
    (async () => {
      try {
        // Get IP address (best effort, with timeout)
        let ipAddress = null;
        try {
          const ipRes = await Promise.race([
            fetch('https://api.ipify.org?format=json'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('IP fetch timeout')), 2000))
          ]) as Response;
          const ipData = await ipRes.json();
          ipAddress = ipData.ip;
        } catch (e) {
          console.warn('Could not fetch IP address');
        }

        // Record acceptance using the DB-authoritative version (not a hardcoded constant)
        const { error } = await Promise.race([
          supabase.rpc('record_terms_acceptance', {
            p_user_id: userId,
            p_version: currentVersion,
            p_ip_address: ipAddress,
            p_user_agent: navigator.userAgent,
            p_marketing_consent: marketingConsent
          }),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('RPC timeout')), 5000))
        ]) as Awaited<ReturnType<typeof supabase.rpc>>;

        if (!error) {
          // DB write confirmed — clear the pending flag
          localStorage.removeItem(pendingKey);
        } else {
          console.error('Terms acceptance RPC error:', error);
          // Flag stays set — will prompt re-acceptance on next login
        }
      } catch (error) {
        console.error('Background terms acceptance recording failed:', error);
        // Flag stays set — will prompt re-acceptance on next login
      }
    })();
  };

  return (
    <Modal open={true} onClose={() => {}} sx={{ zIndex: 10000 }}>
      <ModalDialog
        sx={{
          maxWidth: 700,
          width: '95vw',
          maxHeight: '90vh',
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Gavel sx={{ fontSize: 28, color: 'primary.500' }} />
            <Typography level="h4" sx={{ fontWeight: 700 }}>
              Terms & Conditions
            </Typography>
          </Box>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Please read and accept our terms to continue using ShreemAstro
          </Typography>
        </Box>

        {/* Scrollable Content */}
        <Box
          onScroll={handleScroll}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            '& h3': { fontSize: '1.1rem', fontWeight: 700, mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 },
            '& p': { mb: 1.5, lineHeight: 1.6, fontSize: '0.875rem' },
            '& ul': { pl: 3, mb: 1.5 },
            '& li': { mb: 0.5, fontSize: '0.875rem' }
          }}
        >
          <TermsContent />
        </Box>

        {/* Footer Actions */}
        <Box sx={{ p: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ mb: 2 }}>
            <Checkbox
              label={
                <Typography level="body-sm">
                  <strong>I have read and accept the Terms & Conditions</strong>
                </Typography>
              }
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              disabled={!hasScrolled}
              sx={{ mb: 1 }}
            />
            {!hasScrolled && (
              <Typography level="body-xs" sx={{ color: 'warning.600', ml: 4 }}>
                Please scroll to the bottom to enable acceptance
              </Typography>
            )}
            <Checkbox
              label={
                <Typography level="body-sm">
                  Send me promotional emails about new features and offers (optional)
                </Typography>
              }
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="neutral"
              onClick={onDecline}
              disabled={isSubmitting}
            >
              Decline & Logout
            </Button>
            <Button
              fullWidth
              variant="solid"
              color="primary"
              onClick={handleAccept}
              disabled={!acceptTerms || isSubmitting}
              loading={isSubmitting}
            >
              Accept & Continue
            </Button>
          </Box>

          <Typography level="body-xs" sx={{ textAlign: 'center', mt: 1.5, color: 'text.tertiary' }}>
            © ShreemAstro 2026 | All Rights Reserved
          </Typography>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
